/**
 * Playbooks QA round 6, U5 (item 3) — widened by plan 2026-09-28-006 U3b
 * (rulebook R6) from a sender-only helper to every settings field (sender,
 * CC/reply-to, flow id, rename), shared by every host that gates a settings
 * edit (`SequencesStep.tsx`'s play-flow header and its three popovers,
 * `PlaybooksConfigPage.tsx`'s Library row). Pure of React (no hooks, no
 * `_shared/hooks` import) so `bun test --isolate` can target this module
 * directly — same "stay inside `mini-apps/**`" rule
 * `usage-sidebar-decision-logic.ts`'s own module doc documents. File name
 * kept (rename is churn) even though the module is no longer sender-only.
 *
 * Five functions, one per write shape:
 *  - {@link applySequenceSenderToCache} — the two `setQueryData` patches that
 *    used to live inline in `SequenceSenderPopover.tsx#handleSave` (Codex
 *    review of Playbooks QA round 5, finding B3). Unchanged — still called
 *    directly by the sole-use (ungated) sender write both hosts perform.
 *  - {@link applySettingsToCache} — the generalized cache patch
 *    {@link executeSettingsUpdateAll} uses for every field; delegates to
 *    {@link applySequenceSenderToCache} for `field: 'sender'` (same two
 *    caches, same shape) and patches the same two caches directly for the
 *    other three fields.
 *  - {@link executeSettingsUpdateAll} — decision `'update-all'`/`'update-plays'`
 *    (owned target)'s write: dispatches on `pending.draftSettings.field` to
 *    the matching PATCH mutation. `opts.playId` omitted = a Library write (③,
 *    never scoped to a play — the ownership middleware would refuse a
 *    `?playId=` on a write that isn't); `opts.playId` given = an owned
 *    direct write (② on an owned target, or the ungated sole-use path).
 *  - {@link forkSequenceWithSettings} — "Save a new sequence"'s shared first
 *    half for any field: sender forks via the existing
 *    `duplicateSequenceWithSender` (unchanged — the fork primitive already
 *    takes a sender override); the other three fields duplicate the plain
 *    way and then PATCH the new identity, since the fork primitive has no
 *    cc/replyTo/flowId/name override param. The remap-onto-the-origin-rung
 *    (play-flow header) or append-and-expand (Library) half is host-specific
 *    and stays in each host's own handler.
 *
 * Option ②'s fan-out request body is `pending.draftSettings` itself —
 * `SequenceSettingsDraft` is declared as an `Extract` of
 * `PlaybooksUpdatePlaysRequest['change']` (see that type's own doc in
 * `usage-sidebar-store.ts`), so hosts pass it through directly rather than
 * through an identity-mapping helper (deleted, plan 2026-09-28-007 U10,
 * reflection r-6 — `settingsFanOutChange` returned its argument unchanged).
 */
import type { QueryClient } from '@tanstack/react-query';
// A plain module function, not a React hook — safe from this pure helper the
// same way `usage-sidebar-store.ts` calls it from a store action (see that
// file's own note). The ROOT barrel, not the deep
// `@clay-run/glaze/toast/toast-queue` entry: this file is covered by
// `tsconfig.tests.json`, whose `moduleResolution` cannot resolve Glaze's
// deep entries.
import { addToast } from '@clay-run/glaze';

import type { SequenceSettingsWriteOptions } from './api-client';
import { libraryQueryKey, sequenceDetailQueryKey } from './query-keys';
import {
  duplicateSequence,
  duplicateSequenceWithSender,
  renameSequence,
  setSequenceFlow,
  setSequenceInflectionFields,
  setSequenceSender,
} from './mutations';
import { SAVE_GROUP_FIRST } from './sequence-card-menu';
import type {
  PlaybooksCreateIdentityResponse,
  PlaybooksLibraryResponse,
  PlaybooksSequenceDetailResponse,
} from './schemas';
import { confirmedFanOutSet } from './usage-sidebar-logic';
import type { PendingUsageSidebar, SequenceSettingsDraft } from './usage-sidebar-store';

/** The injected throwing `apiFetch` (`_shared/hooks/useApi.ts`), never imported here. */
type ApiFetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

/**
 * Patches BOTH caches that render a sequence's sender — the library list row
 * and the sequence detail (`SequenceCopyPane`, any rung editor showing this
 * sequence) — then invalidates both as a safety net behind the patch (a
 * concurrent server-side change since the last read still needs a real
 * refetch to surface). Moved out of `SequenceSenderPopover.tsx#handleSave`
 * (Codex review of Playbooks QA round 5, finding B3) so both usage-sidebar
 * hosts can apply the identical patch their own "Update all" handler needs.
 */
export function applySequenceSenderToCache(
  queryClient: QueryClient,
  sequenceId: string,
  sender: string | null,
): void {
  queryClient.setQueryData<PlaybooksLibraryResponse>(libraryQueryKey(), (prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      sequences: prev.sequences.map((seq) => (seq.id === sequenceId ? { ...seq, sender } : seq)),
    };
  });
  queryClient.setQueryData<PlaybooksSequenceDetailResponse>(
    sequenceDetailQueryKey(sequenceId),
    (prev) => (prev ? { ...prev, sequence: { ...prev.sequence, sender } } : prev),
  );
  void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
  void queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(sequenceId) });
}

/** The library-row / sequence-detail field patch for a non-sender settings draft. */
function settingsPatch(draft: Exclude<SequenceSettingsDraft, { field: 'sender' }>): Record<string, unknown> {
  if (draft.field === 'inflection') return { cc: draft.cc, replyTo: draft.replyTo };
  if (draft.field === 'flow') return { flowId: draft.flowId };
  return { name: draft.name };
}

/**
 * Plan 2026-09-28-006 U3b — the generalized cache patch {@link
 * executeSettingsUpdateAll} uses for every field. `field: 'sender'`
 * delegates to {@link applySequenceSenderToCache} verbatim (same two caches,
 * same invalidate-after-patch shape); the other three fields patch the same
 * two caches with their own field(s) directly.
 */
export function applySettingsToCache(
  queryClient: QueryClient,
  sequenceId: string,
  draft: SequenceSettingsDraft,
): void {
  if (draft.field === 'sender') {
    applySequenceSenderToCache(queryClient, sequenceId, draft.sender);
    return;
  }
  const patch = settingsPatch(draft);
  queryClient.setQueryData<PlaybooksLibraryResponse>(libraryQueryKey(), (prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      sequences: prev.sequences.map((seq) => (seq.id === sequenceId ? { ...seq, ...patch } : seq)),
    };
  });
  queryClient.setQueryData<PlaybooksSequenceDetailResponse>(
    sequenceDetailQueryKey(sequenceId),
    (prev) => (prev ? { ...prev, sequence: { ...prev.sequence, ...patch } } : prev),
  );
  void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
  void queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(sequenceId) });
}

/** The one success toast per settings field — matches this app's existing "Step saved."/"Conditions saved." wording. */
function settingsSavedToast(field: SequenceSettingsDraft['field']): string {
  if (field === 'sender') return 'Sender saved.';
  if (field === 'inflection') return 'CC and reply-to saved.';
  if (field === 'flow') return 'Flow saved.';
  return 'Renamed.';
}

/**
 * Plan 2026-09-28-008 U2 (rulebook R5, R8, R14; Codex round-3 F3/F6),
 * TIGHTENED by plan 2026-09-29-001 U1 (R3/R5) — the write options for
 * {@link executeSettingsUpdateAll}. A Library write (no `playId`) is `{
 * playId: undefined }`, unchanged. An owned in-play write also names its
 * group: `rungId` is the origin group, `confirmedRungIds` this play's entry
 * of {@link confirmedFanOutSet} over the places the rep was shown — so a
 * retry after a half-finished "this group only" (the pending was retargeted
 * onto the copy with that group alone as its place) can never quietly reach
 * a second group that started using the copy since: the server refuses it.
 *
 * A staged group (`origin.rungId === null`) can never write in play any
 * more — there is no group yet to name, and the server now refuses an
 * in-play write with none — so this throws {@link SAVE_GROUP_FIRST} instead
 * of silently returning `{ playId }` alone (the exact shape a post-fork
 * silent write used to fall through as, B3).
 */
function settingsWriteOptions(pending: PendingUsageSidebar, playId: string | undefined): SequenceSettingsWriteOptions {
  const { origin } = pending;
  if (playId === undefined || origin.kind === 'library') return { playId: undefined };
  if (origin.rungId === null) throw new Error(SAVE_GROUP_FIRST);
  const rungId = origin.rungId;
  const confirmedRungIds = confirmedFanOutSet(pending.places, rungId).plays.find((entry) => entry.playId === playId)?.rungIds;
  return {
    playId,
    group: confirmedRungIds === undefined || confirmedRungIds.length === 0 ? { rungId } : { rungId, confirmedRungIds },
  };
}

/**
 * "Update all"/"Update N groups in this play" for ANY settings field — a
 * plain PATCH write (the sole-use path's exact write for each field),
 * guarded by `isStillCurrent` (D15, Codex F-B) before touching cache,
 * matching every other usage-sidebar decision handler's own shape.
 *
 * `opts.playId` — plan 2026-09-28-006 U3b (rulebook R6, R9): omitted (or
 * `undefined`) is ALWAYS a template write, with no `?playId=` — reached only
 * via decision `'update-all'` (③, a Library target). A caller passes
 * `opts.playId` only for an OWNED target's ② ("Update N groups in this
 * play") or the ungated sole-use path — forwarding `playId` unconditionally
 * would ask the ownership middleware to guard a Library write that is
 * deliberately NOT scoped to a play.
 *
 * Fires the success toast only after `isStillCurrent` — a superseded request
 * must not toast.
 */
export async function executeSettingsUpdateAll(
  apiFetch: ApiFetch,
  queryClient: QueryClient,
  pending: PendingUsageSidebar,
  isStillCurrent: () => boolean,
  opts?: { playId?: string },
): Promise<void> {
  if (pending.draftSettings === undefined) return;
  const draft = pending.draftSettings;
  const sequenceId = pending.target.id;
  const playOpts = settingsWriteOptions(pending, opts?.playId);
  if (draft.field === 'sender') {
    await setSequenceSender(apiFetch, sequenceId, draft.sender, playOpts);
  } else if (draft.field === 'inflection') {
    await setSequenceInflectionFields(apiFetch, sequenceId, { cc: draft.cc, replyTo: draft.replyTo }, playOpts);
  } else if (draft.field === 'flow') {
    await setSequenceFlow(apiFetch, sequenceId, draft.flowId, playOpts);
  } else {
    await renameSequence(apiFetch, sequenceId, draft.name, playOpts);
  }
  if (!isStillCurrent()) return;
  applySettingsToCache(queryClient, sequenceId, draft);
  addToast({ content: settingsSavedToast(draft.field), status: 'success' });
}

/**
 * The shared first half of "Save as a new sequence" for any settings field:
 * a lineage-clean copy of the source's CURRENT head content, then the new
 * field applied to the fresh identity, plus the library invalidation every
 * fork needs. Returns the created identity so each host's own handler can do
 * its own host-specific second half (the play-flow header's rung remap; the
 * Library row's append-and-expand).
 *
 * `field: 'sender'` reuses `duplicateSequenceWithSender` (the fork primitive
 * already takes a sender override, carrying it atomically with the copy).
 * The other three fields have no such override param on the fork primitive,
 * so this duplicates plainly and then PATCHes the new identity — two writes,
 * same as today's sender path was before it got its own dedicated fork
 * endpoint; acceptable here since a doorway "Save as new" is not gated by
 * `beforeAdopt`'s atomicity requirement (R7 applies to an IN-PLAY fork).
 */
export async function forkSequenceWithSettings(
  apiFetch: ApiFetch,
  queryClient: QueryClient,
  pending: PendingUsageSidebar,
): Promise<PlaybooksCreateIdentityResponse> {
  if (pending.draftSettings === undefined) {
    throw new Error('forkSequenceWithSettings called on a pending request with no draftSettings.');
  }
  const draft = pending.draftSettings;
  let created: PlaybooksCreateIdentityResponse;
  if (draft.field === 'sender') {
    created = await duplicateSequenceWithSender(apiFetch, pending.target.id, draft.sender);
  } else {
    created = await duplicateSequence(apiFetch, pending.target.id);
    if (draft.field === 'inflection') {
      await setSequenceInflectionFields(apiFetch, created.id, { cc: draft.cc, replyTo: draft.replyTo });
    } else if (draft.field === 'flow') {
      await setSequenceFlow(apiFetch, created.id, draft.flowId);
    } else {
      await renameSequence(apiFetch, created.id, draft.name);
    }
  }
  void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
  return created;
}

